import { type ComponentProps } from "react";

import { type PieChartMetric } from "@vega-api/generated/types/pieChartMetric";
import {
  CompositionPieChart,
  type CompositionSegment,
} from "@vega-core/components/charts/PieChart/CompositionPieChart";
import { AnalyticCard } from "@vega-core/components/MetricCard/AnalyticCard";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { formatNumber, Rounding } from "@vega-core/utils/formatNumber";
import { createUrlSlug } from "@vega-core/utils/url.helper";

type LegendVariant = NonNullable<
  ComponentProps<typeof CompositionPieChart>["legendVariant"]
>;

const DEFAULT_LOADING_COUNT = 3;

const toCompositionSegments = (metric: PieChartMetric): CompositionSegment[] =>
  metric.pieChartData.slices.map((slice) => ({
    name: slice.label,
    value: slice.value,
    displayValue: `${formatNumber(slice.percentage, Rounding.Always1Fractions)}%`,
  }));

export const PieChartMetricCard = ({
  metric,
  centerLabel,
  legendVariant = "single",
}: {
  metric: PieChartMetric;
  centerLabel: string;
  legendVariant?: LegendVariant;
}) => (
  <AnalyticCard
    canScreenShot
    dataTestId={`pie-chart-${createUrlSlug(metric.label)}`}
    title={metric.label}
  >
    <CompositionPieChart
      centerLabel={centerLabel}
      centerVariant="total"
      composition={toCompositionSegments(metric)}
      emptyState={[]}
      legendVariant={legendVariant}
    />
  </AnalyticCard>
);

type PieChartMetricsProps = {
  pieChartMetrics: PieChartMetric[];
  centerLabel: string;
  dataTestId: string;
  isLoading?: boolean;
  className?: string;
  legendVariant?: LegendVariant;
  loadingCount?: number;
};

export const PieChartMetrics = ({
  pieChartMetrics,
  centerLabel,
  dataTestId,
  isLoading,
  className,
  legendVariant = "single",
  loadingCount = DEFAULT_LOADING_COUNT,
}: PieChartMetricsProps) => {
  if (!isLoading && pieChartMetrics.length === 0) {
    return null;
  }

  return (
    <div
      className={cnTwMerge(
        "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6",
        className,
      )}
      data-testid={dataTestId}
    >
      {isLoading
        ? Array.from({ length: loadingCount }, (_, index) => index).map(
            (id) => (
              <AnalyticCard
                dataTestId={`pie-chart-loading-${id}`}
                isLoading
                key={id}
                title=""
              >
                <Skeleton className="mx-auto mt-4 h-56 w-56 rounded-full" />
                <div className="mt-4 flex flex-col gap-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </AnalyticCard>
            ),
          )
        : pieChartMetrics.map((metric) => (
            <PieChartMetricCard
              centerLabel={centerLabel}
              key={metric.label}
              legendVariant={legendVariant}
              metric={metric}
            />
          ))}
    </div>
  );
};
